import React, { useEffect, useMemo, useState } from 'react';
// Removed Firebase imports - now using backend APIs
import { auth } from '../firebase'; // Keep auth for user info
import apiClient from '../services/api.js';
import groupReviewsService from '../services/groupReviews.js';

export default function PublicReport() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Load public reports from backend API
  const loadPublicReports = async () => {
    try {
      console.log('[PublicReport] Loading public reports from API...');
      
      const data = await apiClient.get('/public-reports', {
        limit: 100
      });
      
      console.log('[PublicReport] API response:', data);
      
      if (!data) {
        throw new Error('No data received from API');
      }
      
      if (data.success) {
        const reportsList = data.reports || [];
        
        const items = reportsList.map(report => ({
          id: report.id || report._id,
          ...report,
          createdAt: report.createdAt ? new Date(report.createdAt) : null
        }));
        
        setReports(items);
        setError('');
        console.log(`[PublicReport] ✅ Loaded ${items.length} public reports`);
      } else {
        setReports([]);
        const errorMsg = data.message || data.error || 'API returned success: false';
        setError(errorMsg);
        console.log('[PublicReport] ❌ API returned failure:', errorMsg);
      }
    } catch (err) {
      console.error('[PublicReport] ❌ Failed to load public reports:', err);
      
      // Better error handling
      let errorMessage = 'Failed to load public reports';
      if (err.status === 401) {
        errorMessage = 'Authentication failed - please check API credentials';
      } else if (err.status === 404) {
        errorMessage = 'Public reports endpoint not found - may be empty collection';
      } else if (err.data?.detail) {
        errorMessage = err.data.detail;
      } else if (err.message) {
        errorMessage += ': ' + err.message;
      }
      
      setError(errorMessage);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  // Load public reports once on mount
  useEffect(() => {
    loadPublicReports();
    // No polling - user can refresh manually if needed
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return reports;
    const term = search.toLowerCase();
    return reports.filter((r) =>
      (r.groupName || '').toLowerCase().includes(term) ||
      (r.reporterEmail || '').toLowerCase().includes(term) ||
      (r.platform || '').toLowerCase().includes(term)
    );
  }, [reports, search]);

  const { pendingReports, completedReports } = useMemo(() => {
    return {
      pendingReports: filtered.filter(r => !r.done),
      completedReports: filtered.filter(r => r.done)
    };
  }, [filtered]);

  const handleMarkReview = async (item, status) => {
    const reviewLabel = status === 'fake' ? 'fraudulent' : 'genuine';
    const reason = window.prompt(`Please enter reason why this group is ${reviewLabel}:`, '');

    if (reason === null) {
      return;
    }

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      alert('Reason is required before submitting review.');
      return;
    }

    const reviewerId = auth.currentUser?.email || 'unknown_officer';

    const proceed = window.confirm(
      `Confirm marking this group as ${reviewLabel}?\n\nReason: ${trimmedReason}`
    );
    if (!proceed) {
      return;
    }

    try {
      // Create group review using backend API
      const reviewResult = await groupReviewsService.createReview({
        groupLink: item.groupUrl || item.groupLink || '',
        groupName: item.groupName || 'Unknown Group',
        status: status === 'fake' ? 'fake' : 'genuine',
        reason: trimmedReason,
        officerId: reviewerId,
        source: 'public_report_review',
        keywordQuery: item.keywordQuery || item.searchKeyword || '',
        scanReportIds: []
      });

      if (reviewResult && reviewResult.success) {
        alert(`Successfully marked group as ${reviewLabel}!`);
        
        // Mark the item as reviewed locally (since we don't have PATCH endpoint yet)
        setReports(prevReports => 
          prevReports.map(report => 
            report.id === item.id 
              ? { 
                  ...report, 
                  done: true, 
                  reviewStatus: status,
                  reviewReason: trimmedReason,
                  reviewedBy: reviewerId,
                  reviewedAt: new Date()
                }
              : report
          )
        );
      } else {
        throw new Error(reviewResult?.error || 'Failed to create review');
      }
      
    } catch (err) {
      console.error('Failed to save review', err);
      const errorMsg = err.data?.detail || err.message || 'Unknown error';
      alert('Failed to save review: ' + errorMsg);
    }
  };

  return (
    <div className="page-shell public-report-page">
      <header className="top-bar">
        <div>
          <h1 className="page-title">Public Reports</h1>
          <p className="page-subtitle">Recent citizen submissions and summaries.</p>
        </div>
        </header>

      <div className="card input-card public-filter-card">
        <div className="section-head">
          <div>
            <div className="eyebrow">Filter</div>
            <h3 className="section-title">Search reports</h3>
          </div>
          <div className="pill">{filtered.length} shown</div>
        </div>
        <input
          className="form-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by group, reporter, or platform"
        />
      </div>

      <div className="card public-section-card">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ color: '#f59e0b' }}>Action Needed</div>
            <h3 className="section-title">Pending Reports</h3>
          </div>
          {loading ? (
            <div className="pill">Loading</div>
          ) : (
            <div className="pill" style={{ background: '#fef3c7', color: '#b45309' }}>{pendingReports.length} pending</div>
          )}
        </div>

        {error && <div className="error-banner">{error}</div>}
        {!loading && !error && pendingReports.length === 0 && (
          <p className="muted">No pending reports.</p>
        )}

        <div className="list">
          {pendingReports.map((r) => (
            <div className="list-item public-report-item public-report-item-pending" key={r.id}>
              <div className="public-report-row">
                <div className="public-report-main">
                  <div className="list-title public-report-title-row">
                    {r.groupName || 'Unknown Group'}
                    <span className="tag tag-warning" style={{ fontSize: '0.65rem' }}>PENDING</span>
                  </div>
                  {r.groupUrl && (
                    <div className="list-sub public-report-link">
                      <a href={r.groupUrl} target="_blank" rel="noreferrer">{r.groupUrl}</a>
                    </div>
                  )}
                  
                  <div className="public-report-meta-grid">
                    <div><strong>Reporter:</strong> {r.reporterName || "Anonymous"}</div>
                    <div><strong>Email:</strong> {r.reporterEmail || "N/A"}</div>
                  </div>

                  <div className="tag-row public-report-tag-row">
                    {r.platform && <span className="tag">Platform: {r.platform}</span>}
                  </div>
                </div>
                <div className="list-meta public-report-time">
                  {r.createdAt ? r.createdAt.toLocaleString() : 'N/A'}
                </div>
              </div>
              
              <div className="divider public-report-divider" />
              <div className="public-report-actions">
                <button className="action-btn btn-danger" onClick={() => handleMarkReview(r, 'fake')}>
                  Mark Fraudulent
                </button>
                <button className="action-btn btn-success" onClick={() => handleMarkReview(r, 'genuine')}>
                  Mark Genuine
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card public-section-card">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ color: '#059669' }}>Resolved</div>
            <h3 className="section-title">Completed Reports</h3>
          </div>
          {!loading && !error && (
            <div className="pill" style={{ background: '#d1fae5', color: '#065f46' }}>{completedReports.length} completed</div>
          )}
        </div>

        {!loading && !error && completedReports.length === 0 && (
          <p className="muted">No completed reports yet.</p>
        )}

        <div className="list">
          {completedReports.map((r) => (
            <div className="list-item public-report-item public-report-item-completed" key={r.id}>
              <div className="public-report-main">
                <div className="list-title public-report-title-row" style={{ color: '#475569' }}>
                  {r.groupName || 'Unknown Group'}
                  <span className="tag tag-success" style={{ fontSize: '0.65rem' }}>COMPLETED</span>
                </div>
                {r.groupUrl && (
                  <div className="list-sub public-report-link" style={{ opacity: 0.8 }}>
                    <a href={r.groupUrl} target="_blank" rel="noreferrer">{r.groupUrl}</a>
                  </div>
                )}
                
                <div className="public-report-status-row">
                  {r.reviewStatus && (
                    <span className={`tag ${r.reviewStatus === 'fake' ? 'tag-high' : 'tag-success'}`}>
                      {r.reviewStatus === 'fake' ? 'Fraudulent' : 'Genuine'}
                    </span>
                  )}
                  {r.reviewReason && <span className="tag">Reason: {r.reviewReason}</span>}
                  {r.reviewedBy && <span className="tag">By: {r.reviewedBy}</span>}
                  {r.platform && <span className="tag">Platform: {r.platform}</span>}
                </div>
              </div>
              <div className="list-meta public-report-time">
                {r.createdAt ? r.createdAt.toLocaleString() : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

